import { Row, Col, Card, Button, Typography, Alert, Input, Popconfirm } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Loader from "../components/loader";
import "react-toastify/dist/ReactToastify.css";

const Webhooks = () => {
  const { Title, Paragraph, Text } = Typography;
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [secretSet, setSecretSet] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState("");

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    "Content-Type": "application/json",
  });

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}/operation/webhook/config`,
        { headers: authHeaders() }
      );
      const data = response.data.data || {};
      setWebhookUrl(data.webhook_url || "");
      setUrlInput(data.webhook_url || "");
      setSecretSet(Boolean(data.webhook_secret_set));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (error.response && error.response.status === 401) {
        history.push("/sign-in");
      }
    }
  };

  const saveWebhook = async (payload) => {
    try {
      setSaving(true);
      setRevealedSecret("");
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}/operation/webhook/config`,
        payload,
        { headers: authHeaders() }
      );
      const data = response.data.data || {};
      setWebhookUrl(data.webhook_url || "");
      setUrlInput(data.webhook_url || "");
      setSecretSet(Boolean(data.webhook_secret_set));
      if (data.webhook_secret) {
        setRevealedSecret(data.webhook_secret);
      }
      toast.success(response.data.message || "Webhook configuration updated", {
        position: toast.POSITION.TOP_LEFT,
      });
    } catch (error) {
      if (error.response && error.response.status === 401) {
        history.push("/sign-in");
        return;
      }
      toast.error(
        error.response?.data?.message || "Unable to update webhook configuration",
        { position: toast.POSITION.TOP_LEFT }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) {
      toast.error("Enter a webhook URL, or use Disable to clear it", {
        position: toast.POSITION.TOP_LEFT,
      });
      return;
    }
    if (!/^https:\/\//i.test(trimmed)) {
      toast.error("Webhook URL must start with https://", {
        position: toast.POSITION.TOP_LEFT,
      });
      return;
    }
    saveWebhook({ webhook_url: trimmed });
  };

  const handleRegenerateSecret = () => {
    if (!webhookUrl) return;
    saveWebhook({ webhook_url: webhookUrl, regenerate_secret: true });
  };

  const handleDisable = () => {
    saveWebhook({ webhook_url: null });
  };

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="tabled api-tokens-page">
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <Row gutter={[24, 24]}>
          <Col xs={24} xl={14}>
            <Card bordered={false} className="criclebox tablespace mb-24">
              <Title level={3} style={{ marginTop: 0 }}>
                Webhooks
              </Title>
              <Paragraph type="secondary">
                Bulk operations (Buy Talk Time / Send Money bulk uploads) only
                return a total once they finish. Configure a webhook URL here
                to have PaySmart push you the full per-transaction breakdown
                as soon as each bulk job completes.
              </Paragraph>

              {revealedSecret && (
                <Alert
                  type="success"
                  showIcon
                  message="Signing secret generated"
                  description={
                    <div>
                      <Paragraph style={{ marginBottom: 8 }}>
                        Store this now — it won't be shown again. Use it to
                        verify the <Text code>X-PaySmart-Signature</Text>{" "}
                        header on incoming webhook requests.
                      </Paragraph>
                      <Paragraph style={{ marginBottom: 8 }}>
                        <Text code copyable={{ text: revealedSecret }}>
                          {revealedSecret}
                        </Text>
                      </Paragraph>
                    </div>
                  }
                  style={{ marginBottom: 16 }}
                />
              )}

              <Paragraph style={{ marginBottom: 4 }}>
                <Text strong>Webhook URL</Text>
              </Paragraph>
              <Input
                placeholder="https://yourapp.example.com/paysmart/webhook"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                style={{ marginBottom: 12 }}
              />

              <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                {secretSet
                  ? "Signing secret is set (hidden). Save a new URL keeps it unchanged, unless you regenerate it below."
                  : "No signing secret yet — one is generated automatically the first time you save a URL."}
              </Paragraph>

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Button type="primary" loading={saving} onClick={handleSave}>
                  Save
                </Button>
                {webhookUrl && (
                  <Popconfirm
                    title="Generate a new signing secret? Any code verifying the old one will need updating."
                    onConfirm={handleRegenerateSecret}
                    okText="Regenerate"
                    cancelText="Cancel"
                  >
                    <Button loading={saving}>Regenerate secret</Button>
                  </Popconfirm>
                )}
                {webhookUrl && (
                  <Popconfirm
                    title="Disable your webhook? PaySmart will stop sending bulk-job notifications."
                    onConfirm={handleDisable}
                    okText="Disable"
                    cancelText="Cancel"
                  >
                    <Button danger loading={saving}>
                      Disable
                    </Button>
                  </Popconfirm>
                )}
              </div>
            </Card>
          </Col>
          <Col xs={24} xl={10}>
            <Card
              bordered={false}
              className="criclebox mb-24"
            >
              <Title level={5}>How it works</Title>
              <Paragraph>
                After <Text code>bulkairtime</Text> or{" "}
                <Text code>bulkdeposit</Text> finishes, PaySmart sends a
                signed <Text code>POST</Text> to your webhook URL with every
                transaction's status.
              </Paragraph>
              <Paragraph>
                Verify each request with HMAC-SHA256, keyed with your signing
                secret, against the <Text code>X-PaySmart-Signature</Text>{" "}
                header.
              </Paragraph>
              <Paragraph>
                Delivery retries up to 4 times over about 12 minutes if your
                endpoint doesn't respond with a 2xx status. See{" "}
                <Text code>Developers &gt; Documentation</Text> for the full
                payload format.
              </Paragraph>
            </Card>
          </Col>
        </Row>
      )}
      <ToastContainer />
    </div>
  );
};

export default Webhooks;
